import axios from 'axios'
import t from '../languages/es/global.json'
import { CODE_AUTH_EXPIRED, CODE_MULTI_FACTOR_AUTH } from './globalConstants'
import { useSessionStore } from '../store/sessionStore'

async function refreshAuthToken(lang) {
  const refreshToken = useSessionStore.getState().resfreshToken
  const header = {
    'Content-Type': 'application/json',
    Authorization: refreshToken ? 'Bearer ' + refreshToken : '',
    originApp: process.env.REACT_APP_ORIGIN_APP,
    'Accept-Language': lang,
  }
  const redirectUri = process.env.REACT_APP_MICROSOFT_REDIRECT_URI
  const originApp = process.env.REACT_APP_ORIGIN_APP

  const response = await axios.get(
    `${process.env.REACT_APP_AUTH}refresh?redirectUri=${redirectUri}&originApp=${originApp}`,
    { headers: header }
  )

  return {
    token: response.data.accessToken,
    refreshToken: response.data.resfreshToken,
  }
}

export async function apiFetch({
  method = 'GET',
  url,
  data,
  params,
  contentType = 'application/json',
  headerParams = true,
  api = process.env.REACT_APP_API,
  originApp = false,
  successMsg = null,
}) {
  const lang = useSessionStore.getState().language
  const token = useSessionStore.getState().token
  const locationToken = useSessionStore.getState().locationToken
  const user = useSessionStore.getState().user
  const countryCode = useSessionStore.getState().countryCode

  const header = {
    'Content-Type': contentType,
    ...(headerParams
      ? {
        Authorization: token ? 'Bearer ' + token : '',
        ...(originApp
          ? { originApp: process.env.REACT_APP_ORIGIN_APP }
          : { originUser: user?.nickName }),
        'Accept-Language': `${lang}-${countryCode?.toUpperCase()}`,
        ...(api === process.env.REACT_APP_API ? { New: 'true' } : {}),
        ...(locationToken ? { Location: locationToken } : {}),
      }
      : {}),
  }

  try {
    let response
    response = await axios({
      method: method,
      url: api + url,
      ...(params && { params }),
      ...(data && { data }),
      headers: header,
    })
    handleSuccess(successMsg)
    if (response.data) {
      return response.data
    }
  } catch (error) {
    if (error.response && error.response.status === 401) {
      // Token expired, try to refresh
      try {
        const newTokens = await refreshAuthToken(lang)
        // Update store status with new tokens
        useSessionStore.getState().doToken(newTokens.token)
        useSessionStore.getState().doRefreshToken(newTokens.refreshToken)

        // Retry the original request
        return apiFetch({
          method,
          url,
          data,
          params,
          contentType,
          headerParams,
          api,
          originApp,
        })
      } catch (refreshError) {
        // Error handling when refreshing the token
        useSessionStore.getState().doLogout()
        const axiosError = {
          response: refreshError.name,
          message: refreshError.message,
        }
        displayErrorToast(axiosError)
        throw error
      }
    }

    if (error.code === 'ERR_NETWORK' || error.code === 403) {
      useSessionStore.getState().doLogout()
      const axiosError = {
        response: error.name,
        message: error.message,
      }
      displayErrorToast(axiosError)
      throw error
    } else if (error.response.status === 422 || error.response.status === 401) {
      useSessionStore.getState().doLogout()
      const axiosError = {
        response: error.code,
        message: error.response.data.message,
      }
      displayErrorToast(axiosError)
      throw error
    } else if (error.response.status === 404) {
      handleNotFound(error)
    } else if (
      error.response.status === 400 &&
      data.message.includes(CODE_MULTI_FACTOR_AUTH)
    ) {
      const axiosError = {
        response: error.code,
        message: t.error.multiFactorAuth,
      }
      displayErrorToast(axiosError)
      throw error
    } else if (
      error.response.status === 400 &&
      data.message.includes(CODE_AUTH_EXPIRED)
    ) {
      const axiosError = {
        response: error.code,
        message: t.error.codeAuthExpired,
      }
      const cleanUrl =
        window.location.protocol +
        '//' +
        window.location.host +
        window.location.pathname
      window.history.replaceState({}, document.title, cleanUrl)
      displayErrorToast(axiosError)
      throw error
    } else {
      const axiosError = {
        response: error.name,
        message: error.message,
      }
      displayErrorToast(axiosError)
      throw error
    }
  }
}

function handleSuccess(successMsg) {
  if (successMsg) {
    useSessionStore
      .getState()
      .createMsgToast({ body: successMsg, variant: 'positive' })
  }
}

function displayErrorToast(error) {
  useSessionStore.getState().createMsgToast({
    body: error.message,
    title: `Error ${error.response}`,
    variant: 'negative',
  })
}

function handleNotFound(error) {
  const axiosError = {
    response: error.response.status,
    message: `${error.response.request?.statusText}: ${error.response.request?.responseURL}`,
  }
  displayErrorToast(axiosError)
  return axiosError
}

export async function apiFetchIkea({
  method,
  url,
  data,
  params,
  contentType = 'application/json',
}) {
  const header = {
    'Content-Type': contentType,
    'x-client-id': process.env.REACT_APP_X_CLIENT_ID,
  }

  try {
    let response
    response = await axios({
      method: method,
      url: process.env.REACT_APP_API_IKEA + url,
      ...(params && { params }),
      ...(data && { data }),
      headers: header,
    })

    if (response.data) {
      return response.data
    }
  } catch (error) {
    if (error.code === 'ERR_NETWORK') {
      const axiosError = {
        response: error.name,
        message: error.message,
      }
      displayErrorToast(axiosError)
      return axiosError
    } else if (error.response.status === 404) {
      handleNotFound(error)
    } else {
      return error
    }
  }
}
